<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="w-full chart-container">
    <LineChart ref="chartRef" :chart-data="chartData" :chart-options="chartOptions" />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler } from 'chart.js'
import { LineChart } from 'vue-chart-3'
import { formatDate } from '@/utils/format/dateformatting'

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler)

const props = defineProps({
  temperatures: { type: Array, default: () => [] } // Default leeres Array
})

const chartRef = ref(null)

// Colors using App Theme
const css = getComputedStyle(document.documentElement)
const primaryColor = css.getPropertyValue('--color-primary-600').trim()
const primary400 = css.getPropertyValue('--color-primary-400').trim()
const primary200 = css.getPropertyValue('--color-primary-200').trim()
const textColor = css.getPropertyValue('--color-text').trim()

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: { 
      ticks: { color: textColor },
      grid: { color: 'rgba(0,0,0,0.1)' },
      // Set min/max for temperature range (normal goat body temp range)
      min: 37,
      max: 42
    },
    x: { ticks: { color: textColor }, grid: { display: false } }
  },
  plugins: {
    legend: { display: false },
    tooltip: { backgroundColor: 'rgba(0,0,0,0.75)', titleColor: '#fff', bodyColor: '#fff', padding: 10, borderRadius: 8 }
  }
}

// Chart Data
const chartData = ref({
  labels: props.temperatures?.map(t => formatDate(t.measuredAt)) ?? [],
  datasets: [
    {
      label: 'Temperature (°C)',
      data: props.temperatures?.map(t => t.value) ?? [],
      borderColor: primaryColor,
      borderWidth: 2,
      pointRadius: 4,
      tension: 0.3,
      fill: true,
      backgroundColor: primaryColor + '33' //fallback because gradient is not working as intended needs a proper fix
    }
  ]
})

// Background gradient with opacity
function setGradient() {
  const chart = chartRef.value?.chart
  if (!chart) return
  const ctx = chart.ctx
  const gradient = ctx.createLinearGradient(0, 0, 0, chart.height)
  gradient.addColorStop(0, primary400 + '88')
  gradient.addColorStop(1, primary200 + '00')
  chart.data.datasets[0].backgroundColor = gradient
  chart.update()
}

// Watch props.temperatures. If data changes set gradient again as well as chart data
// Dates sorted from least to most current
watch(
  () => props.temperatures,
  async (newTemperatures) => {
    const sorted = [...(newTemperatures ?? [])].sort(
      (a, b) => new Date(a.measuredAt) - new Date(b.measuredAt)
    )

    chartData.value.labels = sorted.map(t => formatDate(t.measuredAt))
    chartData.value.datasets[0].data = sorted.map(t => t.value)

    await nextTick()
    setGradient()
  },
  { immediate: true }
)


</script>

<style scoped>
.chart-container {
  min-height: 200px;
}
</style>
